import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Grid,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { DataGrid, Toolbar } from '@mui/x-data-grid';
import Layout from '../layout/layout';
import {api}from '../services/api';
import { useToast } from "../components/ToastContext";
import 'react-toastify/dist/ReactToastify.css';
import SearchComponent from '../components/Search';
import { evaluateAndReturn } from '../Utils/common';
// Custom hook to consolidate template state management
const useTemplateState = () => {
  const [templates, setTemplates] = useState([]);
  const [filteredTemplates, setFilteredTemplates] = useState([]);
  const [loading, setLoading] = useState(false);

  const filterTemplates = (data, term) => {
    const lowerTerm = term.toLowerCase();
    return data.filter((template) => {
      const templateName = template.name || '';
      const createdBy = template.createdBy || '';
      return templateName.toLowerCase().includes(lowerTerm) || createdBy.toLowerCase().includes(lowerTerm);
    });
  };

  const handleSearchChange = (newSearchTerm) => {
    setFilteredTemplates(filterTemplates(templates, newSearchTerm));
  };

  return { templates, setTemplates, filteredTemplates, loading, setLoading, handleSearchChange };
};

// Custom hook for form validation state
const useFormValidation = () => {
  const [invalidFields, setInvalidFields] = useState({});
  const [error, setError] = useState('');
  return { invalidFields, setInvalidFields, error, setError };
};

// Custom hook for dialog state management
const useDialogState = () => {
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [formData, setFormData] = useState({});
  return { openEditDialog, setOpenEditDialog, selectedTemplate, setSelectedTemplate, formData, setFormData };
};

const ApplicationTemplate = () => {
  const theme = useTheme();
  const toast = useToast();
  const navigate = useNavigate();
  
  // Consolidated states using custom hooks
  const templateState = useTemplateState();
  const validationState = useFormValidation();
  const dialogState = useDialogState();
  
  // Destructure all state variables and setters from custom hooks
  const { templates, setTemplates, filteredTemplates, loading, setLoading, handleSearchChange } = templateState;
  const { invalidFields, setInvalidFields, error, setError } = validationState;
  const { openEditDialog, setOpenEditDialog, selectedTemplate, setSelectedTemplate, formData, setFormData } = dialogState;
  
  const [vmSizes, setVmSizes] = useState([]);
  const [vmSizeSeries, setVmSizeSeries] = useState([]);
  const [formFields, setFormFields] = useState([]);

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    templateState.setLoading(true);
    const userId = sessionStorage.getItem('userId');

    try {
      const response = await api.get(`/api/Application/getAllTemplate/${userId}`);
      const data = response.data;
      if (Array.isArray(data)) {
        templateState.setTemplates(data);
        templateState.setFilteredTemplates(data);
      } else {
        console.error('Unexpected response format or missing data:', data);
        validationState.setError('Unexpected response format');
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
      validationState.setError('Failed to fetch templates');
    } finally {
      templateState.setLoading(false);
    }
  };

  const handleAddNewTemplate = () => {
    navigate('/application_template/operating_system');
  };

  const handleEditTemplate = async (template) => {
    setLoading(true);
    setSelectedTemplate(template);

    try {
      const response = await api.get(`/api/Application/getTemplate/${template.templateId}`, {
        headers: {
          'X-CORRELATION-ID': 'd0b9c2e6-25d3-42da-922a-2944aa9d97f4',
        },
      });

      const templateData = response.data;
      const templateRequest = JSON.parse(templateData.templateRequestJson);

      setFormData({
        name: templateData.name || '',
        TemplateName: templateRequest.TemplateName || '',
        ResourceGroup: templateRequest.ResourceGroup || '',
        Subscription: templateRequest.Subscription || '',
        location: templateRequest.location || '',
        OS: templateRequest.OS || '',
        OsImage: templateRequest.OsImage || '',
        Tier: templateRequest.Tier || '',
        VMSizeSeries: templateRequest.VMSizeSeries || '',
        VMSize: templateRequest.VMSize || '',
        Tag: templateRequest.Tag || '',
      });

      const fieldsResponse = await api.get('/api/Application/form/124/4');
      setFormFields(fieldsResponse.data.templateId);

      setOpenEditDialog(true);
    } catch (error) {
      console.error('Error fetching template details:', error);
      setError('Failed to fetch template details');
    } finally {
      setLoading(false);
    }
  };

  // Helper: Check if deletion was successful
  const isDeleteSuccess = (response) => response.status === 200;

  // Helper: Update templates list after deletion
  const removeDeletedTemplate = (templateId) => {
    const filtered = templates.filter((template) => template.templateId !== templateId);
    setTemplates(filtered);
  };

  // Helper: Handle successful template deletion
  const onDeleteSuccess = (templateId) => {
    removeDeletedTemplate(templateId);
    toast.success('Template deleted successfully.');
  };

  // Helper: Handle delete error
  const onDeleteError = (error) => {
    console.error('Error deleting template:', error);
    toast.error(error.message || 'Error deleting template.');
  };

  // Helper: Execute delete request
  const executeDeleteTemplate = async (templateId) => {
    try {
      const response = await api.post(`/api/Application/template/${templateId}`, {
        headers: {
          'X-CORRELATION-ID': 'your-correlation-id-here',
        },
      });
      if (isDeleteSuccess(response)) {
        onDeleteSuccess(templateId);
      }
    } catch (error) {
      onDeleteError(error);
    } finally {
      fetchTemplates();
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    await executeDeleteTemplate(templateId);
  };

  // Helper: Update form data and reset invalid state
  const updateFormState = (fieldName, value) => {
    setFormData((prevData) => ({ ...prevData, [fieldName]: value }));
    setInvalidFields((prevInvalid) => ({ ...prevInvalid, [fieldName]: false }));
  };

  // Helper: Fetch and set VM sizes for location
  const handleLocationChange = async (value) => {
    setLoading(true);
    try {
      const response = await api.get(`/api/Application/vmSize/${encodeURIComponent(value)}`);
      setVmSizes(response.data);
    } catch (error) {
      console.error('Error fetching VM sizes:', error);
      setError('Failed to fetch VM sizes');
    } finally {
      setLoading(false);
    }
  };

  // Helper: Fetch VM size series for specific VM size and location
  const handleVMSizeChange = async (value) => {
    const location = formData['location'];
    if (!location) return;

    setLoading(true);
    try {
      const response = await api.get(
        `/api/Application/vmSize/${encodeURIComponent(location)}/${encodeURIComponent(value)}`
      );
      setVmSizeSeries(response.data);
    } catch (error) {
      console.error('Error fetching VM size series:', error);
      setError('Failed to fetch VM size series');
    } finally {
      setLoading(false);
    }
  };

  // Helper: Route field change to appropriate handler
  const routeFieldChange = async (fieldName, value) => {
    if (fieldName === 'location') {
      await handleLocationChange(value);
    }
    if (fieldName === 'VMSize') {
      await handleVMSizeChange(value);
    }
  };

  const handleChange = async (fieldName, value) => {
    updateFormState(fieldName, value);
    await routeFieldChange(fieldName, value);
  };

  const handleUpdateTemplate = async () => {
    setLoading(true);

    const newInvalidFields = {};
    formFields.forEach((field) => {
      if (field.isRequired && !formData[field.fieldName]) {
        newInvalidFields[field.fieldName] = true;
      }
    });

    if (Object.keys(newInvalidFields).length > 0) {
      setLoading(false);
      setInvalidFields(newInvalidFields);
      return;
    }

    const applicationRequest = JSON.stringify({
      TemplateName: formData['TemplateName'] || '',
      ResourceGroup: formData['ResourceGroup'] || 2,
      Subscription: formData['Subscription'] || '8f7d862b-5995-401f-a514-7f9f1f9ebae5',
      location: formData['location'] || 'eastus',
      OS: formData['OS'] || 1,
      OsImage: formData['OsImage'] || 1,
      Tier: formData['Tier'] || 1,
      VMSizeSeries: formData['VMSizeSeries'] || 'DCadsv5-seriesWindows',
      VMSize: formData['VMSize'] || 'aa263fa2-ba6e-4671-0447-08dd8bb14330',
      Tag: formData['Tag'] || null,
    });

    try {
      const requestBody = {
        templateName: formData['TemplateName'] || '',
        subscription: formData['Subscription'] || '8f7d862b-5995-401f-a514-7f9f1f9ebae5',
        applicationRequest: applicationRequest,
        requester: formData['Requester'] || '2',
        region: formData['location'] || '1',
        resourceType: Number.parseInt(formData['ResourceType'] || '124'),
      };

      const response = await api.post(
        `/api/Application/updateTemplate/${selectedTemplate.templateId}`,
        requestBody,
        {
          headers: {
            'X-CORRELATION-ID': 'your-correlation-id-here',
          },
        }
      );

      if (response.status === 200) {
        toast.success('Template updated successfully!');
        setOpenEditDialog(false);
        setTemplates(
          templates.map((template) =>
            template.templateId === selectedTemplate.templateId ? { ...template, ...formData } : template
          )
        );
        fetchTemplates();
      }
    } catch (error) {
      toast.error(error.message ||'Failed to update template!');
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    { field: 'name', headerName: 'Template Name', flex: 1 },
    { field: 'createdBy', headerName: 'Created By', flex: 1 },
    {
      field: 'actions',
      headerName: 'Actions',
      flex: 1,
      filterable: false, // Disable filtering for the actions column
sortable: false, 
      disableColumnMenu: true,
      renderCell: (params) => (
        <>
         <Box
                  component="span"
                  sx={{
                    bgcolor: '#e6f4ea', // Light green background
                    color: '#2e7d32', // Dark green text
                    border: '1px solid #2e7d32',
                    borderRadius: '4px',
                    padding: '2px 8px',
                    marginRight: '8px',
                    cursor: 'pointer',
                  }}
                  onClick={() => handleEditTemplate(params.row)}
                >
                  Edit
                </Box>
                <Box
                  component="span"
                  sx={{
                    bgcolor: '#ffebee', // Light red background
                    color: '#d32f2f', // Dark red text
                    border: '1px solid #d32f2f',
                    borderRadius: '4px',
                    padding: '2px 8px',
                    cursor: 'pointer',
                  }}
                  onClick={() => handleDeleteTemplate(params.row.templateId)}
                >
                  Delete
                </Box>
         
        </>
      ),
    },
  ];

  const renderField = (field) => {
    if (!field.isDisplay) return null;

    const label = field.isRequired ? `${field.fieldLabel} *` : field.fieldLabel;
    const isError = invalidFields[field.fieldName];

    if (field.fieldType.toLowerCase() === "text") {
      return (
        <Grid item xs={12} sm={6} key={field.fieldId} sx={{ width: '49%' }}>
          <TextField
            label={label}
            required={field.isRequired}
            fullWidth
            error={isError}
            helperText={isError ? `${label} is required` : ''}
            value={formData[field.fieldName] || ''}
            onChange={(e) => handleChange(field.fieldName, e.target.value)}
            sx={{
              '& .MuiInputBase-root': {
                height: '50px',
                boxShadow: 'none',
              },
              '& .MuiInputLabel-root': {
                shrink: true,
              },
            }}
          />
        </Grid>
      );
    } else if (field.fieldType.toLowerCase() === "dropdown" && field.dropdownValues) {
      const dropdownValues = JSON.parse(field.dropdownValues);
      return (
        <Grid item xs={12} sm={6} key={field.fieldId} sx={{ width: '49%' }}>
          <FormControl fullWidth error={isError}>
            <InputLabel>{label}</InputLabel>
            <Select
              required={field.isRequired}
              label={label}
              value={formData[field.fieldName] || ''}
              onChange={(e) => handleChange(field.fieldName, e.target.value)}
            >
              {dropdownValues.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
            {isError && <Typography color="error">{`${label} is required`}</Typography>}
          </FormControl>
        </Grid>
      );
    }
    return null;
  };

  

  return (
    <Layout pageType="sideBar">
      <Box sx={{ flex: 1, pl: 2, bgcolor: theme.palette.background.default, color: theme.palette.text.primary }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: "center", mb: 1 }}>
          {/* <Typography variant="h6">Template</Typography> */}
          <Box sx={{width:'67%'}}>
          <SearchComponent onSearchChange={handleSearchChange} />
          </Box>
          <Button variant="contained" sx={{ color: "white", bgcolor: "#00aaff" }} onClick={() => handleAddNewTemplate()}>
            Add New Template
          </Button>
        </Box>


        <Box sx={{  width: '100%', mt: 1 }}>
          {loading ? (
            <CircularProgress />
          ) : (
            <DataGrid
              rows={filteredTemplates}
              columns={columns}
              pageSize={5}
              rowsPerPageOptions={[5, 10, 20]}
              disableSelectionOnClick
              hideFooterSelectedRowCount 
              getRowId={(row) => row.templateId}
              slots={{
    toolbar: Toolbar, 
  }}
              sx={{
                '& .MuiDataGrid-root': {
              bgcolor: evaluateAndReturn(theme.palette.mode === 'dark', '#212330', '#ffffff'), // Adjust background color based on theme
            },
                '& .MuiDataGrid-filler': {
      display: 'none',
    },
                bgcolor: evaluateAndReturn(theme.palette.mode === 'dark', '#212330', '#2b61aa'), // Use black for dark mode and white for light mode
                '& .MuiDataGrid-columnHeaders': {
                  bgcolor: evaluateAndReturn(theme.palette.mode === 'dark', '#212330', '#2b61aa'), // Adjust header background color
                },
                '& .MuiDataGrid-columnHeaderTitle': {
                  fontWeight: 'bold !important',
                  // textTransform: "uppercase",
            
                },
                '& .MuiDataGrid-cell': {
                  color: evaluateAndReturn(theme.palette.mode === 'dark', '#ffffff', '#212330'), // Adjust text color based on theme
                  borderBottomWidth: '1px solid',
                  borderColor:evaluateAndReturn(theme.palette.mode === 'dark', '#212330', '#2b61aa'),
            
                },
                '& .MuiDataGrid-row:nth-of-type(even)': {
                  bgcolor: evaluateAndReturn(theme.palette.mode === 'dark', '#2a2d3c', '#fafafa'), // Adjust even row background color
                },
                '& .MuiDataGrid-row:nth-of-type(odd)': {
                  bgcolor: evaluateAndReturn(theme.palette.mode === 'dark', '#212330', '#ffffff'), // Adjust odd row background color
                },
                '& .MuiDataGrid-footerContainer': {
                  bgcolor: evaluateAndReturn(theme.palette.mode === 'dark', '#2a2d3c', '#f0f0f0'), // Adjust footer background color
                },
              }}
            />
          )}
        </Box>
      </Box>

      <Dialog open={openEditDialog} onClose={() => setOpenEditDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ bgcolor: theme.palette.background.default }}>Edit Template</DialogTitle>
        <DialogContent sx={{ bgcolor: theme.palette.background.default }}>
          <Box
            component="form"
            sx={{
              pl: 2,
              pr: 2,
              mt: 2,
              bgcolor: theme.palette.background.default,
              color: theme.palette.text.primary,
            }}
          >
            {loading && <CircularProgress size={24} sx={{ justifyContent: "center", textAlign: "center", display: 'flex' }} />}
            {error && <Box className="error-message">{error}</Box>}
            <Grid container spacing={2} sx={{ width: '100%', bgcolor: theme.palette.background.default, mt: 2 }}>
              {formFields.map((field) => renderField(field))}
              {vmSizes.length > 0 && (
                <Grid item xs={12} sm={6} sx={{ width: '49%', bgcolor: theme.palette.background.default }}>
                  <FormControl>
                    <InputLabel>VM Size Series</InputLabel>
                    <Select
                      required
                      label="VM Size"
                      value={formData['VMSize'] || ''}
                      onChange={(e) => handleChange('VMSize', e.target.value)}
                    >
                      {vmSizes.map((size) => (
                        <MenuItem key={size.value} value={size.value}>
                          {size.text}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}
              {vmSizeSeries.length > 0 && (
                <Grid item xs={12} sm={6} sx={{ width: '100%' }}>
                  <FormControl>
                    <InputLabel>VM Size</InputLabel>
                    <Select
                      required
                      label="VM Size Series"
                      value={formData['VMSizeSeries'] || ''}
                      onChange={(e) => handleChange('VMSizeSeries', e.target.value)}
                    >
                      {vmSizeSeries.map((series) => (
                        <MenuItem key={series.value} value={series.value}>
                          {series.text}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}
            </Grid>
          </Box>
        </DialogContent>

        <DialogActions sx={{ bgcolor: theme.palette.background.default }}>
          <Button variant='cancelContained' onClick={() => setOpenEditDialog(false)} 
          // color="default"
          >
            Cancel
          </Button>
          <Button variant='greenContained' onClick={() => handleUpdateTemplate()} 
          // color="primary"
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
};

export default ApplicationTemplate;


